// Sends a GA4 "store_click" event whenever a visitor clicks a link to the App
// Store or Google Play, wherever it appears on the site, and tags the link with
// campaign parameters so installs can be traced back to the page and to how the
// visitor reached the site. Links to the web app (app.voiatravel.com) get the
// same treatment as a "web_app_click" event with utm parameters. One delegated listener means a store link added to
// any page later is tracked and tagged without touching it.
//
// gtag itself is loaded from public/index.html. If it is missing (blocked by an
// ad blocker, say) the click is not recorded, but the link is still tagged.

import { WEB_APP_HOST } from './webApp';

const APP_STORE_ID = '6747190886';
const PLAY_PACKAGE = 'com.voia.travel.mobile';

// App Store Connect only reports a campaign (ct) when it comes with the
// provider token (pt) from a campaign link generated in App Store Connect >
// App Analytics. Until it is set here, App Store links carry no campaign.
const APP_STORE_PROVIDER_TOKEN = '';

const ENTRY_KEY = 'voia.entrySource';

const STORES = [
  { host: 'apps.apple.com', name: 'app_store' },
  { host: 'play.google.com', name: 'google_play' },
];

function storeFor(href) {
  try {
    const { hostname } = new URL(href, window.location.href);
    const match = STORES.find((s) => hostname === s.host);
    return match ? match.name : null;
  } catch {
    return null;
  }
}

// Where on the page the link sits, so the report shows whether the navbar, the
// footer or the body copy drives installs.
function locationOf(link) {
  const tagged = link.closest('[data-track-location]');
  if (tagged) return tagged.dataset.trackLocation;
  if (link.closest('nav, header')) return 'navbar';
  if (link.closest('footer')) return 'footer';
  return 'content';
}

// How the visitor first reached the site in this session, from the referrer of
// the landing page. Read once and kept for the session, because by the time
// they click a store link the referrer is our own page.
function classifyReferrer(referrer) {
  let host = '';
  try {
    host = referrer ? new URL(referrer).hostname : '';
  } catch {
    host = '';
  }
  if (!host) return 'direct';
  if (host === window.location.hostname) return 'internal';
  if (/(^|\.)(google|bing|duckduckgo|yahoo|ecosia|yandex|baidu|brave)\./.test(host)) return 'organic';
  if (/(^|\.)(chatgpt\.com|openai\.com|perplexity\.ai|claude\.ai|gemini\.google\.com|copilot\.microsoft\.com)$/.test(host)) {
    return 'ai';
  }
  if (/(^|\.)(facebook|instagram|tiktok|reddit|t|x|twitter|linkedin|pinterest|youtube)\.(com|co)$/.test(host)) return 'social';
  return 'referral';
}

function rememberEntrySource() {
  try {
    if (!window.sessionStorage.getItem(ENTRY_KEY)) {
      window.sessionStorage.setItem(ENTRY_KEY, classifyReferrer(document.referrer));
    }
  } catch {
    // Storage blocked: entrySource() falls back to the live referrer.
  }
}

function entrySource() {
  try {
    const stored = window.sessionStorage.getItem(ENTRY_KEY);
    if (stored) return stored;
  } catch {
    // Fall through.
  }
  return classifyReferrer(document.referrer);
}

// The page the click happened on, without ids: shared trip, profile and invite
// links collapse to their type so campaigns stay countable. Those links land on
// the home path (/?trip=…), so they are checked before it.
function campaignFor(pathname) {
  const { search } = window.location;
  if (/^\/trip\//.test(pathname) || /[?&]trip=/.test(search)) return 'trip-share';
  if (/^\/p\//.test(pathname) || /[?&]profile=/.test(search)) return 'profile-share';
  if (/^\/i\//.test(pathname) || /[?&]invite=/.test(search)) return 'invite';
  if (pathname === '/') return 'home';
  return pathname.replace(/^\/+|\/+$/g, '').replace(/[^a-z0-9]+/gi, '-').toLowerCase().slice(0, 60);
}

// The store link to send this click to: region-neutral App Store URL (Apple
// opens the visitor's own storefront) and campaign parameters on both stores.
export function taggedStoreUrl(store, { campaign, medium, content }) {
  if (store === 'app_store') {
    const url = new URL(`https://apps.apple.com/app/id${APP_STORE_ID}`);
    if (APP_STORE_PROVIDER_TOKEN) {
      url.searchParams.set('pt', APP_STORE_PROVIDER_TOKEN);
      url.searchParams.set('ct', `${medium}-${campaign}`.slice(0, 40));
      url.searchParams.set('mt', '8');
    }
    return url.toString();
  }
  const referrer = new URLSearchParams({
    utm_source: 'voiatravel.com',
    utm_medium: medium,
    utm_campaign: campaign,
    utm_content: content,
  }).toString();
  const url = new URL('https://play.google.com/store/apps/details');
  url.searchParams.set('id', PLAY_PACKAGE);
  url.searchParams.set('referrer', referrer);
  return url.toString();
}

function isWebAppLink(href) {
  try {
    return new URL(href, window.location.href).hostname === WEB_APP_HOST;
  } catch {
    return false;
  }
}

function handleWebAppClick(link) {
  const linkLocation = locationOf(link);
  const medium = entrySource();
  const campaign = campaignFor(window.location.pathname);
  const url = new URL(link.getAttribute('href'), window.location.href);
  url.searchParams.set('utm_source', 'voiatravel.com');
  url.searchParams.set('utm_medium', medium);
  url.searchParams.set('utm_campaign', campaign);
  url.searchParams.set('utm_content', linkLocation);
  link.href = url.toString();

  if (typeof window.gtag !== 'function') return;
  window.gtag('event', 'web_app_click', {
    link_location: linkLocation,
    page_path: window.location.pathname,
    entry_source: medium,
    transport_type: 'beacon',
  });
}

function handleClick(event) {
  const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (!link) return;
  if (isWebAppLink(link.getAttribute('href'))) {
    handleWebAppClick(link);
    return;
  }
  const store = storeFor(link.getAttribute('href'));
  if (!store) return;

  const linkLocation = locationOf(link);
  const medium = entrySource();
  // Rewritten before the browser follows the link, so the tagged URL is the
  // one that opens.
  link.href = taggedStoreUrl(store, {
    campaign: campaignFor(window.location.pathname),
    medium,
    content: linkLocation,
  });

  if (typeof window.gtag !== 'function') return;
  window.gtag('event', 'store_click', {
    store,
    link_location: linkLocation,
    page_path: window.location.pathname,
    entry_source: medium,
    // Optional attribution set on the link (e.g. the shared itinerary page).
    ...(link.dataset.trackSource ? { source: link.dataset.trackSource } : {}),
    ...(link.dataset.shareToken ? { share_token: link.dataset.shareToken } : {}),
    transport_type: 'beacon',
  });
}

export function trackStoreClicks() {
  rememberEntrySource();
  document.addEventListener('click', handleClick, true);
  // Middle-click opens a new tab without a click event.
  document.addEventListener('auxclick', handleClick, true);
}
